/**
 * MicPermission.jsx — Public help page targeted by the QR code that the
 * MyCompass iOS app displays on its MicPermissionBlockedBanner (GMA-1361).
 *
 * Audience: caregivers. The disabled client scans the QR with the
 * caregiver's phone, the caregiver lands here, and this page walks them
 * through the iPad's Settings → MyCompass → Microphone toggle.
 *
 * Must be reachable UNAUTHENTICATED — the caregiver may not be logged
 * into compasscare.app on the phone they're scanning with.
 *
 * GMA-1363 — Mic permission lifecycle, web side (parent: GMA-1360)
 */

import { useEffect } from "react";

// Step content is a pure data array so the test file can iterate it and
// pin every step's text + accessibility id without needing screenshots.
export const MIC_PERMISSION_HELP_STEPS = [
  {
    id: "step-1",
    title: "Open Settings on the iPad",
    body:
      "Find the grey gear icon labeled 'Settings'. It's on the iPad's Home Screen. " +
      "If you can't find it, swipe down from the middle of the screen and type 'Settings'.",
  },
  {
    id: "step-2",
    title: "Scroll to MyCompass",
    body:
      "In Settings, scroll the left-hand list until you find 'MyCompass'. " +
      "Apps appear in alphabetical order — it'll be in the M section.",
  },
  {
    id: "step-3",
    title: "Tap MyCompass, then turn Microphone ON",
    body:
      "You'll see a list of permissions. Find 'Microphone' and toggle it ON " +
      "(the switch turns green when it's on). That's it — the iPad will tell " +
      "MyCompass automatically. No restart needed.",
  },
  {
    id: "step-4",
    title: "Return to MyCompass",
    body:
      "Press the Home button (or swipe up from the bottom) to leave Settings, " +
      "then open MyCompass. The red banner should disappear within a few seconds. " +
      "Calls should work again straight away.",
  },
];

const MicPermission = () => {
  useEffect(() => {
    // Page title for caregivers who land here via QR scan — helpful in
    // browser-history breadcrumbs.
    document.title = "MyCompass — Enable Microphone Help";
  }, []);

  return (
    <main
      data-testid="mic-permission-help-page"
      style={{
        maxWidth: "720px",
        margin: "0 auto",
        padding: "24px 20px 60px",
        fontFamily: "-apple-system, system-ui, sans-serif",
        lineHeight: 1.5,
        color: "#1c1c1e",
        // GMA-1363: App.css centers .App — override so step instructions read left-aligned.
        textAlign: "left",
      }}
    >
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: "28px", margin: "0 0 8px" }}>
          Help your client enable Microphone access
        </h1>
        <p style={{ margin: 0, color: "#444", fontSize: "16px" }}>
          Your client&apos;s iPad currently has microphone access turned off for
          MyCompass. Without it, they can&apos;t make or receive calls. Follow the
          four steps below on the iPad to fix it — it takes about 30 seconds.
        </p>
      </header>

      <div
        role="note"
        data-testid="mic-permission-help-banner"
        style={{
          background: "#fff5f5",
          border: "2px solid #e53935",
          borderRadius: 12,
          padding: "12px 16px",
          marginBottom: 24,
          color: "#c62828",
        }}
      >
        <strong>Note:</strong> these steps must be done on the iPad your client
        is using — not on this phone. If you&apos;re not near the iPad, you can read
        the steps aloud over the phone.
      </div>

      <ol style={{ padding: 0, margin: 0, listStyle: "none" }}>
        {MIC_PERMISSION_HELP_STEPS.map((step, idx) => (
          <li
            key={step.id}
            data-testid={`mic-permission-help-${step.id}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              padding: "16px 0",
              borderBottom:
                idx === MIC_PERMISSION_HELP_STEPS.length - 1
                  ? "none"
                  : "1px solid #e5e5ea",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: 18,
                background: "#007aff",
                color: "#fff",
                fontWeight: 700,
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {idx + 1}
            </div>
            <div>
              <h2 style={{ fontSize: "18px", margin: "0 0 4px" }}>
                {step.title}
              </h2>
              <p style={{ margin: 0, fontSize: "15px", color: "#3a3a3c" }}>
                {step.body}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <section
        aria-labelledby="still-stuck-heading"
        style={{
          marginTop: 32,
          padding: 16,
          background: "#f2f2f7",
          borderRadius: 12,
        }}
      >
        <h2 id="still-stuck-heading" style={{ fontSize: "18px", margin: "0 0 8px" }}>
          Still stuck?
        </h2>
        <p style={{ margin: 0, fontSize: "15px" }}>
          If the red banner doesn&apos;t go away after you&apos;ve turned Microphone on,
          close the MyCompass app fully (swipe up from the bottom and swipe the
          MyCompass card up off the screen), then re-open it.
        </p>
      </section>

      <footer style={{ marginTop: 32, fontSize: "12px", color: "#8e8e93" }}>
        GMA-1363 · CompassCare ·{" "}
        <a href="https://compasscare.app" style={{ color: "#007aff" }}>
          compasscare.app
        </a>
      </footer>
    </main>
  );
};

export default MicPermission;
